/**
 * OTA update logic for Capacitor native apps.
 *
 * Uses the custom AppUpdater native plugin to download new frontend bundles
 * from the Knocknoc server and apply them. The native plugin handles zip
 * extraction, file storage, and WebView path switching.
 *
 * Flow:
 *   1. On startup, call ready() to confirm the current bundle is healthy
 *   2. Check the server for a newer bundle version
 *   3. If the server's minNativeVersion is higher than the APK's version,
 *      trip the force-update gate and skip applying the bundle
 *   4. If the bundle's buildVersion is older than the last-applied one,
 *      skip (downgrade protection)
 *   5. Otherwise, if the hash differs, download and apply (reloads the WebView)
 *
 * The server endpoints are:
 *   GET /api/app/bundle           - returns { hash, buildVersion, minNativeVersion }
 *   GET /api/app/bundle/download  - returns the zip file
 */

import { ref, type Ref } from "vue";

import { isNativePlatform } from "./platform";
import { getServerUrl } from "./server-url";
import { setForceUpdateActive } from "./force-update-state";
import { compareVersions, isBelowMinimumVersion } from "./version-compare";
import { router } from "../routes-user";
import type { PluginListenerHandle } from "@capacitor/core";

interface BundleVersionInfo {
  hash: string;
  buildVersion?: string;
  minNativeVersion?: string;
}

const LAST_BUILD_VERSION_KEY = "knocknoc_last_bundle_build_version";

export type UpdateStatus = "idle" | "downloading" | "failed";

/**
 * Reactive status for the OTA update pipeline. `downloading` covers the
 * download + extract phase; `applying` transitions to a WebView reload so
 * a UI state is not needed for it (the user will see the reload). `failed`
 * is transient and auto-clears after a short delay.
 */
export const updateStatus: Ref<UpdateStatus> = ref("idle");

const FAILED_AUTO_CLEAR_MS = 5000;
let _failedClearTimer: ReturnType<typeof setTimeout> | null = null;

function setStatus(s: UpdateStatus): void {
  if (_failedClearTimer) {
    clearTimeout(_failedClearTimer);
    _failedClearTimer = null;
  }
  updateStatus.value = s;
  if (s === "failed") {
    _failedClearTimer = setTimeout(() => {
      if (updateStatus.value === "failed") updateStatus.value = "idle";
    }, FAILED_AUTO_CLEAR_MS);
  }
}

let _initialized = false;
let _checkInProgress = false;
let _listenerHandle: PluginListenerHandle | null = null;

/**
 * Initialise the OTA updater. Safe to call multiple times; only runs once.
 * Call this from main-user.ts after the app mounts.
 */
export async function initAppUpdater(): Promise<void> {
  if (!isNativePlatform() || _initialized) return;
  _initialized = true;

  const { default: AppUpdater } = await import("./app-updater-plugin");

  // Mark the current bundle as healthy so the native plugin doesn't roll it back
  await AppUpdater.ready();

  // Check for updates on startup
  await checkForUpdate(AppUpdater);

  // Check again each time the app resumes from background
  const { App } = await import("@capacitor/app");
  _listenerHandle = await App.addListener("appStateChange", (state) => {
    if (state.isActive) {
      checkForUpdate(AppUpdater);
    }
  });
}

export async function removeAppUpdaterListener(): Promise<void> {
  if (_listenerHandle) {
    await _listenerHandle.remove();
    _listenerHandle = null;
  }
}

/**
 * Trigger an update check. Called from setServerUrl() when the user
 * configures the server URL for the first time.
 */
export async function triggerUpdateCheck(): Promise<void> {
  if (!isNativePlatform()) return;
  const { default: AppUpdater } = await import("./app-updater-plugin");
  await checkForUpdate(AppUpdater);
}

/**
 * Check for an update and wait for it to complete.
 * Returns true if an update was applied (WebView will reload), false otherwise.
 */
export async function checkForUpdateAndAwait(): Promise<boolean> {
  if (!isNativePlatform()) return false;
  const { default: AppUpdater } = await import("./app-updater-plugin");
  return await checkForUpdate(AppUpdater);
}

async function checkForUpdate(updater: typeof import("./app-updater-plugin").default): Promise<boolean> {
  if (_checkInProgress) return false;
  _checkInProgress = true;
  try {
    const serverUrl = await getServerUrl();
    if (!serverUrl) {
      console.log("[AppUpdater] No server URL, skipping update check");
      return false;
    }

    const response = await fetch(`${serverUrl}/api/app/bundle?_=${encodeURIComponent(String(Date.now()))}`, {
      method: "GET",
      cache: "no-store",
      signal: AbortSignal.timeout(10000),
    });
    if (!response.ok) {
      console.log(`[AppUpdater] Bundle endpoint returned ${response.status}`);
      return false;
    }

    const latest: BundleVersionInfo = await response.json();

    // Force-update gate: if the server requires a newer native app than we
    // are running, trip the router guard and stop. isBelowMinimumVersion is
    // fail-open: an empty minNativeVersion means all versions are compatible,
    // and an unparseable version on either side (dev builds) does not gate.
    if (latest.minNativeVersion) {
      const nativeVersion = await getNativeAppVersion();
      if (isBelowMinimumVersion(nativeVersion, latest.minNativeVersion)) {
        console.log(
          `[AppUpdater] Native app ${nativeVersion} is below required ${latest.minNativeVersion}; forcing update`,
        );
        await activateForceUpdate();
        return false;
      }
    }

    // Sanitize hash for use as a directory name (base64 hashes can contain "/")
    const safeHash = latest.hash.replace(/\//g, "_");
    const current = await updater.current();
    console.log(`[AppUpdater] Current: ${current.id}, Server: ${safeHash}`);

    // If the server hash matches the running bundle, no update needed
    if (current.id === safeHash) {
      console.log("[AppUpdater] Hashes match, no update needed");
      return false;
    }

    // Downgrade protection: if we have previously applied a bundle whose
    // buildVersion is newer than the one the server is offering, refuse to
    // downgrade. Hash comparison above guarantees we only reach here on a
    // differing bundle.
    if (latest.buildVersion && (await isDowngrade(latest.buildVersion))) {
      console.log(`[AppUpdater] Server bundle ${latest.buildVersion} is older than last applied; refusing downgrade`);
      return false;
    }

    // Download the new bundle
    console.log(`[AppUpdater] Downloading bundle ${safeHash}...`);
    setStatus("downloading");
    const bundle = await updater.download({
      url: `${serverUrl}/api/app/bundle/download`,
      version: safeHash,
    });

    // Apply immediately - this reloads the WebView with the new bundle.
    // The new bundle's startup code will call ready() to confirm it's healthy.
    console.log(`[AppUpdater] Applying bundle ${bundle.id}...`);
    await updater.apply({ id: bundle.id, path: bundle.path });
    if (latest.buildVersion) {
      await rememberAppliedBuildVersion(latest.buildVersion);
    }
    return true;
  } catch (e) {
    console.log(`[AppUpdater] Update check failed: ${e}`);
    setStatus("failed");
    return false;
  } finally {
    _checkInProgress = false;
  }
}

async function activateForceUpdate(): Promise<void> {
  setForceUpdateActive();
  try {
    if (router.currentRoute.value.name !== "ForceUpdate") {
      await router.push({ name: "ForceUpdate" });
    }
  } catch (e) {
    console.log(`[AppUpdater] Could not navigate to ForceUpdate route: ${e}`);
  }
}

async function getNativeAppVersion(): Promise<string | null> {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return info.version ?? null;
  } catch (e) {
    console.log(`[AppUpdater] Could not read native app version: ${e}`);
    return null;
  }
}

async function isDowngrade(incoming: string): Promise<boolean> {
  const { Preferences } = await import("@capacitor/preferences");
  const { value } = await Preferences.get({ key: LAST_BUILD_VERSION_KEY });
  if (!value) return false;
  const cmp = compareVersions(incoming, value);
  // Unparseable means "cannot determine" - do not block.
  if (cmp === null) return false;
  return cmp < 0;
}

async function rememberAppliedBuildVersion(version: string): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: LAST_BUILD_VERSION_KEY, value: version });
  } catch (e) {
    console.log(`[AppUpdater] Could not persist applied bundle version: ${e}`);
  }
}
